import { lineFont } from '../state';
import { ObjectFormat } from '../uiObjects/objectFormat';
import { isEmptyOrSpace } from '../utils/isEmptyOrSpace';
import { isNumeric } from '../utils/isNumeric';
import { onlyLetters } from '../utils/onlyLetters';

import { buildAbsoluteValue } from './AbsoluteValue';
import { buildBarred } from './Barred';
import { Direction } from './direction';
import { buildFraction, Fraction } from './Fraction';
import { buildFunction, Function as GmmFunction } from './Function';
import type { Line } from './Line';
import { MathObject } from './MathObject';
import { buildPower, Power } from './Power';
import { buildQuantity, Quantity } from './Quantity';
import { buildRoot, Root } from './Root';
import { buildSubscript, Subscript } from './Subscript';
import {
  buildText,
  DEFAULT_SOLO_TEXT_WIDTH,
  DEFAULT_SPACER_TEXT_WIDTH,
  MIDDLE_SYMBOLS,
  Text,
  TextDependencies,
} from './Text';

const FRACTION_CAPTURE_BOUNDARY_SYMBOLS = [
  ' ',
  ...MIDDLE_SYMBOLS,
  '+',
  '-',
  '\u2212',
  '*',
  '\u00F7',
];
const FRACTION_CAPTURE_UNARY_SIGN_SYMBOLS = ['-', '\u2212'];

export interface MultiDependencies extends TextDependencies {
  getCurrentAttemptGetter: () => any;
  maybeUndoSelectedLineEntryButton(): void;
  reportErrorIncludingAttemptSoFar: (msg: string) => void;
}

export interface InsertRequestedOptions {
  absorbLeftIntoNumerator?: boolean;
  captureFunctionContext?: boolean;
}

export class Multi extends MathObject {
  problemJS: MultiDependencies;
  line: Line;

  constructor(problemJS: MultiDependencies, line: Line) {
    super();
    this.problemJS = problemJS;
    this.line = line;

    this.gmmName = 'multi';
    this.t = 'm';
  }

  setEditable(b: boolean): void {
    for (let a = 0; a < this.getValueChildren().length; a++) {
      this.getValueChildren()[a].setEditable(b);
    }
  }

  getLineX(): number {
    if (this.getValueParent()?.t === 'line') return 0;

    const grandparent = this.getValueParent()?.parent as Multi;

    return this.getValueParent()!.viewportX + grandparent.getLineX();
  }

  // RELIES ON top level line parent to recursively work resizing
  setFont(font: string): void {
    for (let a = 0; a < this.getValueChildren().length; a++) {
      this.getValueChildren()[a].setFont(font);
    }
  }

  setFontSize(fontSize: number): void {
    for (let a = 0; a < this.getValueChildren().length; a++) {
      const child = this.getValueChildren()[a];

      if (child instanceof Text || child instanceof Multi) {
        (child as Text | Multi).setFontSize(fontSize);
      } else this.getValueChildren()[a].setFont(fontSize + 'px ' + lineFont);
    }
  }

  balanceEmptyTextsRecursive(): void {
    // skip down through intervening Fraction/Power/Quantity...
    for (let i = 0; i < this.getValueChildren().length; i++) {
      const child = this.getValueChildren()[i];

      if (child.t !== 'text') {
        for (let j = 0; j < child.children.length; j++) {
          const grandchild = child.children[j];

          if (grandchild instanceof Multi) {
            grandchild.balanceEmptyTextsRecursive();
          }
        }
      }
    }

    this.balanceEmptyTexts();
  }

  hasMultiLinesText(): boolean {
    for (let i = 0; i < this.getValueChildren().length; i++) {
      const child = this.getValueChildren()[i];

      if (!(child instanceof Text)) {
        for (let j = 0; j < child.children.length; j++) {
          const grandchild = child.children[j];

          if (grandchild instanceof Multi) {
            const has = grandchild.hasMultiLinesText();

            if (has) return true;
          }
        }
      } else {
        if (child.lines && child.lines.length > 1) return true;
      }
    }

    return false;
  }

  isEmptyOrSpace(): boolean {
    for (let i = 0; i < this.getValueChildren().length; i++) {
      const child = this.getValueChildren()[i];

      if (!(child instanceof Text)) {
        for (let j = 0; j < child.children.length; j++) {
          const grandchild = child.children[j];

          if (grandchild instanceof Multi) {
            if (!grandchild.isEmptyOrSpace()) return false;
          }
        }
      } else {
        if (!isEmptyOrSpace(child.text)) return false;
      }
    }

    return true;
  }

  setLine(line: Line): void {
    this.line = line;

    for (let x = 0; x < this.getValueChildren().length; x++) {
      this.getValueChildren()[x].setLine(line);
    }
  }

  toXML(): string {
    let sb = '';

    for (let i = 0; i < this.getValueChildren().length; i++) {
      sb += this.getValueChildren()[i].toXML();
    }

    return sb;
  }

  swapString(old: string, replacer: string): void {
    for (let i = 0; i < this.getValueChildren().length; i++) {
      this.getValueChildren()[i].swapString(old, replacer);
    }
  }

  balanceEmptyTexts(): void {
    // don't pad base of power with empty text elements on left and right
    if (this.getValueChildren().length == 1 && this.gmmName === 'base multi') {
      return;
    }

    // first, merge any adjoining
    let lastWasText = false;

    for (let iii = 0; iii < this.getValueChildren().length; iii++) {
      const children = this.getValueChildren();
      const child = children[iii];

      if (lastWasText) {
        if (child instanceof Text) {
          const first = this.getValueChildren()[iii - 1] as Text;

          const formatFirst = first.getObjectFormat();
          const formatChild = child.getObjectFormat();

          if (
            !child.isEmptyAndUnformatted() &&
            !first.isEmptyAndUnformatted()
          ) {
            if (!ObjectFormat.areMergeCompatible(formatFirst, formatChild)) {
              continue;
            }
          }

          first.text += child.text;

          if (child.multiline) {
            first.multiline = true;
            first.maxW = child.maxW;
          }

          first.setObjectFormat(ObjectFormat.merge(formatFirst, formatChild));

          // remove merged child
          this.getValueChildren().splice(iii, 1);

          // decrement so that this slot is reconsidered after later children slide down
          iii--;
        }
      }

      lastWasText = child instanceof Text;
    }

    // second, ensure c sequencing of blank texts
    lastWasText = false;

    for (let i = 0; i < this.getValueChildren().length; i++) {
      const child = this.getValueChildren()[i];

      if (!(child instanceof Text)) {
        if (!lastWasText && !this.skipInjectionOfAdjacentEmptyText(child)) {
          const insert = buildText(this.problemJS);

          insert.e = true;
          // code to insert at i
          this.add(insert, i);
          // decrement so that this slot (now with text) is processed again
          i--;
        } else {
          lastWasText = false;
        }
      } else {
        lastWasText = true;
      }
    }

    const lastChild = this.getValueChildren()[
      this.getValueChildren().length - 1
    ];

    if (
      !(lastChild instanceof Text) &&
      !this.skipInjectionOfAdjacentEmptyText(lastChild)
    ) {
      const last = buildText(this.problemJS);

      last.e = true;
      this.add(last);
    }

    // last, set all minW to default OR 'empty'
    if (this.getValueChildren().length === 1) {
      (this.getValueChildren()[0] as Text).minWidth = DEFAULT_SOLO_TEXT_WIDTH;
    } else {
      for (let i = 0; i < this.getValueChildren().length; i++) {
        const child = this.getValueChildren()[i];

        if (child instanceof Text) {
          child.minWidth = DEFAULT_SPACER_TEXT_WIDTH;
        }
      }
    }

    for (let i = 0; i < this.getValueChildren().length; i++) {
      const child = this.getValueChildren()[i];

      if (child instanceof Text) {
        child.sizeMe(true);
      }
    }

    this.childSizeChanged();
  }

  skipInjectionOfAdjacentEmptyText(child: MathObject): boolean {
    if (child instanceof Quantity) {
      if (this.parent?.gmmName === 'power') {
        const power = this.parent as Power;
        const base = power.base;
        const baseChild = base.children[0];

        if (baseChild === child) {
          return true;
        }
      }
    }

    return false;
  }

  getElementToLeft(me: MathObject): MathObject | undefined {
    let index = -1;

    const valueChildren = this.getValueChildren();

    for (let i = 0; i < valueChildren.length; i++) {
      if (me === valueChildren[i]) {
        index = i;
        break;
      }
    }

    return index < 1 ? undefined : this.getValueChildren()[index - 1];
  }

  getElementToRight(me: MathObject): MathObject | undefined {
    let index = -1;

    for (let i = 0; i < this.getValueChildren().length; i++) {
      if (me === this.getValueChildren()[i]) {
        index = i;
        break;
      }
    }

    return index == -1 || index == this.getValueChildren().length - 1
      ? undefined
      : this.getValueChildren()[index + 1];
  }

  private insertEmptyTextToRight(me: MathObject): Text | undefined {
    let index = -1;

    for (let i = 0; i < this.getValueChildren().length; i++) {
      if (me === this.getValueChildren()[i]) {
        index = i;
        break;
      }
    }

    if (index === -1) {
      return undefined;
    }

    const inserted = buildText(this.problemJS);

    inserted.e = true;
    this.add(inserted, index + 1);

    return inserted;
  }

  insertMenuFunction(srcText: Text): GmmFunction | undefined {
    return this.insertRequested('function', srcText, {
      captureFunctionContext: false,
    }) as GmmFunction | undefined;
  }

  pushTextOutOfBaseToLeft(power: MathObject, text: string): void {
    const valueToLeft = this.getElementToLeft(power);

    if (valueToLeft instanceof Text) {
      valueToLeft.text += text;
      valueToLeft.sizeMe();
      valueToLeft.setCursorIndentChars(text.length);
    } else {
      this.problemJS.getProblemContext().addStackableDialog?.({
        msg: 'GMM entry error, please restart',
        reload: true,
        top: 'Error',
      });
    }
  }

  pushTextOutToRight(parent: MathObject, text: string): void {
    let n = this.getElementToRight(parent);

    if (!(n instanceof Text)) {
      const valueParent = this.getValueParent();

      if (valueParent instanceof Power && valueParent.base === this) {
        const grandparentMulti = valueParent.parent as Multi | undefined;

        if (grandparentMulti) {
          n = grandparentMulti.getElementToRight(valueParent);

          if (!(n instanceof Text)) {
            n = grandparentMulti.insertEmptyTextToRight(valueParent);
          }

          if (n instanceof Text) {
            n.text += text;
            n.sizeMe();

            grandparentMulti.moveCursor(valueParent, 'right');
            n.setCursorIndentChars(text.length, true);

            return;
          }
        }
      }
    }

    if (!(n instanceof Text)) {
      n = this.insertEmptyTextToRight(parent);
    }

    if (n instanceof Text) {
      n.text += text;
      n.sizeMe();

      this.moveCursor(parent, 'right');
      n.setCursorIndentChars(text.length, true);
    } else {
      this.problemJS.reportErrorIncludingAttemptSoFar(
        `Cannot "pushTextOutToRight" during insert of ${text} because no textToRight exists.
         Instead, we have ${n ? n.gmmName : 'nothing'}.
        `
      );

      this.problemJS.getProblemContext().addStackableDialog?.({
        msg: 'GMM entry error, rebooting',
        reload: true,
        reloadIsInternal: true,
        top: 'Error',
      });
    }
  }

  deleteLeft(srcText: MathObject): void {
    // index of src
    let index = -1;

    for (let i = 0; i < this.getValueChildren().length; i++) {
      if (srcText === this.getValueChildren()[i]) {
        index = i;
        break;
      }
    }

    if (index < 2 || index == -1) {
      return;
    }

    // remove child at index - 1
    // slide everyone else down
    this.getValueChildren().splice(index - 1, 1);
    this.getValueChildren()[index - 2].grabFocus('left');
    this.balanceEmptyTexts();
    this.line?.notifySizeChanged();
  }

  moveCursor(child: any, direction: Direction): boolean {
    const parent = this.getValueParent();

    if (direction === 'down' || direction === 'up') {
      let m = false;

      const isPowerBaseUp =
        parent instanceof Power && this === parent.base && direction === 'up';
      const isPowerExpDown =
        parent instanceof Power && this === parent.exp && direction === 'down';
      const isRootRadUp =
        parent instanceof Root && this === parent.rad && direction === 'up';
      const isRootRootDown =
        parent instanceof Root && this === parent.root && direction === 'down';
      const isSubscriptBaseDown =
        parent instanceof Subscript &&
        this === parent.base &&
        direction === 'down';
      const isSubscriptSubUp =
        parent instanceof Subscript &&
        this === parent.sub &&
        direction === 'up';
      const isFunctionTypeOrValueDown =
        parent instanceof GmmFunction &&
        (this === parent.type || this === parent.value) &&
        direction === 'down';
      const isFunctionBaseUp =
        parent instanceof GmmFunction &&
        this === parent.base &&
        direction === 'up';

      if (parent?.t === 'line') {
        m = false;
      } else if (
        !(parent instanceof Fraction) &&
        !isPowerBaseUp &&
        !isPowerExpDown &&
        !isRootRadUp &&
        !isRootRootDown &&
        !isSubscriptBaseDown &&
        !isSubscriptSubUp &&
        !isFunctionTypeOrValueDown &&
        !isFunctionBaseUp
      ) {
        const multiGrandparent = this.parent!.parent as Multi;

        m = multiGrandparent.moveCursor(this.parent, direction);
      } else if (parent instanceof Fraction) {
        if (direction === 'down' && this === parent.numerator) {
          parent.denominator.grabFocus();
          m = true;
        }

        if (direction === 'up' && this === parent.denominator) {
          parent.numerator.grabFocus();
          m = true;
        }
      } else if (parent instanceof Power) {
        if (direction === 'down') {
          parent.base.grabFocus('left');
          m = true;
        } else {
          parent.exp.grabFocus('right');
          m = true;
        }
      } else if (parent instanceof Root) {
        if (parent.showRoot() && direction === 'up') {
          parent.root.grabFocus('left');
          m = true;
        } else {
          parent.rad.grabFocus('right');
          m = true;
        }
      } else if (parent instanceof Subscript) {
        if (direction === 'down') {
          parent.sub.grabFocus('right');
          m = true;
        } else {
          parent.base.grabFocus('left');
          m = true;
        }
      } else if (parent instanceof GmmFunction) {
        if (direction === 'down') {
          if (parent.showBase) {
            if (this === parent.type) parent.base.grabFocus('left');
            else parent.base.grabFocus('right');
          }

          m = true;
        } else {
          if (child.cursorIndentChars == 0) parent.type.grabFocus('left');
          else parent.value.grabFocus('right');
          m = true;
        }
      }

      if (m) return m;

      const currentAttemptGetter = this.problemJS.getCurrentAttemptGetter();

      if (
        currentAttemptGetter &&
        currentAttemptGetter.gmmName === 'normalGetter' &&
        this.problemJS.getFocused()
      ) {
        const ls = currentAttemptGetter.lines;

        if (ls.length > 0) {
          let i = -1;

          for (let a = 0; a < ls.length; a++) {
            if (this.problemJS.getFocused()?.line === ls[a]) {
              i = a;
              break;
            }
          }

          if (i > -1) {
            let d;

            if (direction === 'up') {
              if (i === 0) return false;
              d = -1;
            } else {
              if (i == ls.length - 1) {
                ls[i].normalGetter.nextLine();

                return false;
              }

              d = 1;
            }

            i += d;
            const tl = ls[i];
            const y = tl.viewportY + (d > 0 ? 3 : tl.viewportH - 3);
            const x = Math.max(5, this.problemJS.getFocused()!.getLineX());

            tl.normalGetter.mouseDown(x, y, this.problemJS.paintCanvas);
          }
        }
      }

      return false;
    }

    // LEFT OR RIGHT

    // get index of src
    let index = -1;

    for (let i = 0; i < this.getValueChildren().length; i++) {
      if (child === this.getValueChildren()[i]) {
        index = i;
      }
    }

    if (index == -1) {
      return false;
    }

    if (direction === 'left') {
      if (index < 1) {
        if (parent?.t === 'line') {
          return false;
        } else {
          let moved = false;

          if (parent instanceof Power) {
            if (this === parent.exp) {
              parent.base.grabFocus('left');
              moved = true;
            }
          } else if (parent instanceof Root) {
            if (this === parent.rad && parent.showRoot()) {
              parent.root.grabFocus('left');
              moved = true;
            }
          } else if (parent instanceof Subscript) {
            if (this === parent.sub) {
              parent.base.grabFocus('left');
              moved = true;
            }
          } else if (parent instanceof GmmFunction) {
            if (this === parent.base) {
              parent.type.grabFocus('left');
              moved = true;
            } else if (this === parent.value) {
              if (parent.showBase) {
                parent.base.grabFocus('left');
                moved = true;
              } else {
                parent.type.grabFocus('left');
                moved = true;
              }
            }
          }

          const multiGrandparent = this.parent!.parent! as Multi;

          if (!moved) return multiGrandparent.moveCursor(this.parent, 'left');

          return true;
        }
      } else {
        this.getValueChildren()[index - 1].grabFocus('left');

        return true;
      }
    }

    // RIGHT
    else {
      if (index >= this.getValueChildren().length - 1) {
        if (this.getValueParent()?.t === 'line') {
          return false;
        } else {
          let moved = false;

          if (parent instanceof Power) {
            if (this === parent.base) {
              parent.exp.grabFocus('right');
              moved = true;
            }
          } else if (parent instanceof Root) {
            if (this === parent.root) {
              parent.rad.grabFocus('right');
              moved = true;
            }
          } else if (parent instanceof Subscript) {
            if (this === parent.base) {
              parent.sub.grabFocus('right');
              moved = true;
            }
          } else if (parent instanceof GmmFunction) {
            if (this === parent.type) {
              if (parent.showBase) {
                parent.base.grabFocus('right');
                moved = true;
              } else {
                parent.value.grabFocus('right');
                moved = true;
              }
            } else if (this === parent.base) {
              parent.value.grabFocus('right');
              moved = true;
            }
          }

          if (!moved) {
            const multiGrandparent = this.parent!.parent! as Multi;

            return multiGrandparent.moveCursor(this.parent, 'right');
          }

          return true;
        }
      } else {
        this.getValueChildren()[index + 1].grabFocus('right');

        return true;
      }
    }
  }

  deleteRight(srcText: Text): void {
    let index = -1;

    for (let i = 0; i < this.getValueChildren().length; i++) {
      if (srcText === this.getValueChildren()[i]) {
        index = i;
        break;
      }
    }

    if (index == this.getValueChildren().length - 1 || index == -1) {
      return;
    }

    // remove child at index
    // slide everyone else down
    // sizeMe, not recursive
    this.getValueChildren().splice(index + 1, 1);
    this.balanceEmptyTexts();
    this.line?.notifySizeChanged();
  }

  private buildCapturedTextLike(source: Text, text: string): Text {
    const built = buildText(
      this.problemJS,
      0,
      0,
      text,
      source.fontSize,
      source.multiline,
      source.maxW,
      false
    );

    built.setObjectFormat(source.getObjectFormat());
    built.setFont(source.font);
    built.e = source.e;

    return built;
  }

  private populateCapturedFractionNumerator(
    insertAsFraction: Fraction,
    capturedParts: MathObject[]
  ): void {
    if (capturedParts.length === 0) {
      return;
    }

    const numerator = insertAsFraction.numerator;

    numerator.children = [];

    for (const part of capturedParts) {
      numerator.add(part);
    }

    numerator.balanceEmptyTexts();
    insertAsFraction.autoCapturedNumerator = true;
  }

  private getFractionCaptureStart(text: string): number {
    for (let a = text.length - 1; a >= 0; a--) {
      const symbol = text[a];

      if (
        FRACTION_CAPTURE_BOUNDARY_SYMBOLS.includes(symbol) &&
        !this.isUnaryFractionCaptureSign(text, a)
      ) {
        return a + 1;
      }
    }

    return 0;
  }

  private isUnaryFractionCaptureSign(text: string, index: number): boolean {
    if (
      !FRACTION_CAPTURE_UNARY_SIGN_SYMBOLS.includes(text[index]) ||
      index === text.length - 1
    ) {
      return false;
    }

    if (index === 0) {
      return true;
    }

    return FRACTION_CAPTURE_BOUNDARY_SYMBOLS.includes(text[index - 1]);
  }

  private captureFractionNumeratorFromLeft(
    srcText: Text,
    index: number,
    preString: string,
    insertAsFraction: Fraction
  ): {
    index: number;
    textToLeftOfInsert: string;
  } {
    if (preString.length === 0 && index > 0) {
      const left = this.getValueChildren()[index - 1];

      if (left instanceof Fraction) {
        return {
          index,
          textToLeftOfInsert: '',
        };
      }
    }

    const capturedParts: MathObject[] = [];
    const currentCaptureStart = this.getFractionCaptureStart(preString);
    const currentCapture = preString.substring(currentCaptureStart);
    const textToLeftOfInsert = preString.substring(0, currentCaptureStart);
    let foundBoundary = currentCaptureStart > 0;

    if (currentCapture.length > 0) {
      capturedParts.push(this.buildCapturedTextLike(srcText, currentCapture));
    }

    while (!foundBoundary && index > 0) {
      const left = this.getValueChildren()[index - 1];

      if (left instanceof Text) {
        const captureStart = this.getFractionCaptureStart(left.text);
        const capturedText = left.text.substring(captureStart);
        const remainingText = left.text.substring(0, captureStart);

        if (capturedText.length > 0) {
          if (captureStart === 0) {
            capturedParts.unshift(left);
            this.getValueChildren().splice(index - 1, 1);
            index--;
          } else {
            capturedParts.unshift(
              this.buildCapturedTextLike(left, capturedText)
            );
            left.text = remainingText;
            left.sizeMe();
            foundBoundary = true;
          }
        } else if (captureStart === 0 && left.text.length === 0) {
          this.getValueChildren().splice(index - 1, 1);
          index--;
        } else {
          left.text = remainingText;
          left.sizeMe();
          foundBoundary = captureStart > 0;
        }
      } else {
        capturedParts.unshift(left);
        this.getValueChildren().splice(index - 1, 1);
        index--;
      }
    }

    srcText.text = textToLeftOfInsert;
    srcText.setCursorIndentChars(srcText.text.length, true);
    this.populateCapturedFractionNumerator(insertAsFraction, capturedParts);

    return {
      index,
      textToLeftOfInsert,
    };
  }

  insertRequested(
    t:
      | 'fraction'
      | 'barred'
      | 'quantity'
      | 'absoluteValue'
      | 'power'
      | 'subscript'
      | 'root'
      | 'function',
    srcText: Text,
    options: InsertRequestedOptions = {}
  ): MathObject | undefined {
    let count = 0;

    let ancestor = this.getValueGrandparent();

    // Shouldn't need to check for instanceof MathObject,
    // but we had a bug where a grandparent was not a MathObject
    // (did not have getValueGrandparent function): PLAT-4501.
    // We suspect cause is buried somewhere in megafile.
    while (ancestor instanceof MathObject) {
      count++;
      ancestor = ancestor.getValueGrandparent();
    }

    if (count > 3) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const parent = this.getValueParent();
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const intoBase = parent instanceof Power && parent.base === this;

    if (intoBase && t === 'power') return;

    let index = -1;

    for (let i = 0; i < this.getValueChildren().length; i++) {
      if (srcText === this.getValueChildren()[i]) {
        index = i;
        break;
      }
    }

    const text = srcText.text;
    const preString =
      text.length === 0 || srcText.cursorIndentChars === 0
        ? ''
        : text.substring(0, srcText.cursorIndentChars);
    const postString =
      text.length === 0 || srcText.cursorIndentChars == text.length
        ? ''
        : text.substring(srcText.cursorIndentChars);

    let textToLeftOfInsert = preString;

    srcText.text = textToLeftOfInsert;

    let insert: MathObject;

    if (t === 'fraction') {
      insert = buildFraction(this.problemJS, this.line);
      const insertAsFraction = insert as Fraction;

      if (options.absorbLeftIntoNumerator) {
        const capture = this.captureFractionNumeratorFromLeft(
          srcText,
          index,
          preString,
          insertAsFraction
        );

        index = capture.index;
        textToLeftOfInsert = capture.textToLeftOfInsert;
      }
    } else if (t == 'barred') {
      insert = buildBarred(this.problemJS, this.line);
    } else if (t === 'quantity') {
      insert = buildQuantity(this.problemJS, this.line);
    } else if (t === 'absoluteValue') {
      insert = buildAbsoluteValue(this.problemJS, this.line);
    } else if (t === 'power') {
      insert = buildPower(this.problemJS, this.line);
      const insertAsPower = insert as Power;

      if (preString.length > 0) {
        const ll = preString.substring(preString.length - 1);
        let possibleAbbreviation = false;

        if (preString.length > 1 && ll === '.') {
          const previousCharacter = preString.substring(
            preString.length - 2,
            preString.length - 1
          );

          possibleAbbreviation = /^[a-zA-Z]*$/.test(previousCharacter);
        }

        if (/^[a-zA-Z\u03C0]*$/.test(ll) || possibleAbbreviation) {
          srcText.text = preString.substring(0, preString.length - 1);
          const baseText = insertAsPower.base.children[0] as Text;

          baseText.text = ll;
          baseText.sizeMe();
        } else if (isNumeric(ll)) {
          let built = ll;
          let stop = preString.length - 1;

          for (let myI = preString.length - 2; myI > -1; myI--) {
            const sn = preString.substring(myI, myI + 1);

            if (isNumeric(sn) || sn == '.') {
              built = sn + built;
              stop--;
            } else {
              break;
            }
          }

          srcText.text = preString.substring(0, stop);
          const baseText = insertAsPower.base.children[0] as Text;

          baseText.text = built;
          baseText.sizeMe();
        }
      } else if (index > 0) {
        const left = this.getValueChildren()[index - 1];

        // don't let powered values be raised to powers...
        // ...student should use parens for wrapping if power-to-a-power is desired
        if (left.t === 'power') {
          return;
        } else if (left.t !== 'text') {
          insertAsPower.base.children[0] = left;
          left.parent = insertAsPower.base;
          this.getValueChildren().splice(index - 1, 1);
          index--;
        }
      }
    } else if (t === 'subscript') {
      insert = buildSubscript(this.problemJS, this.line);
      const insertAsSubscript = insert as Subscript;

      if (preString.length > 0) {
        // Find characters left of the new subscript
        let lastChar = preString.length - 1;

        while (onlyLetters(preString[lastChar]) && lastChar >= 0) {
          lastChar--;
        }

        // Last character is always one greater
        lastChar++;

        // Try to find a base for the subscript
        const subscriptBase =
          preString.length - lastChar > 0
            ? preString.substring(lastChar, preString.length)
            : '';
        const previous = preString.substring(0, lastChar);

        // Split text and resize
        srcText.text = previous;
        srcText.sizeMe();
        const subscriptBaseText = insertAsSubscript.base.children[0] as Text;

        subscriptBaseText.text = subscriptBase;
        subscriptBaseText.sizeMe();
      } else if (index > 0) {
        const left = this.getValueChildren()[index - 1];

        if (left.t !== 'text') {
          insertAsSubscript.base.children[0] = left;
          left.parent = insertAsSubscript.base;
          this.getValueChildren().splice(index - 1, 1);
          index--;
        }
      }
    } else if (t === 'function') {
      insert = buildFunction(this.problemJS, this.line);
      const insertAsFunction = insert as GmmFunction;
      const captureFunctionContext = options.captureFunctionContext ?? true;

      if (captureFunctionContext && preString.length > 0) {
        // Find characters left of the new function
        let lastChar = preString.length - 1;

        while (onlyLetters(preString[lastChar]) && lastChar >= 0) {
          lastChar--;
        }

        // Last character is always one greater
        lastChar++;

        // Try to find a type for the function
        const type =
          preString.length - lastChar > 0
            ? preString.substring(lastChar, preString.length)
            : '';
        const previous = preString.substring(0, lastChar);

        // Split text and resize
        srcText.text = previous;
        srcText.sizeMe();
        const typeText = insertAsFunction.type.children[0] as Text;

        typeText.text = type;
        typeText.sizeMe();
      } else if (captureFunctionContext && index > 0) {
        const left = this.getValueChildren()[index - 1];

        if (!(left.t === 'text')) {
          insertAsFunction.type.children[0] = left;
          left.parent = insertAsFunction.type;
          this.getValueChildren().splice(index - 1, 1);
          index--;
        }
      }
    } else if (t === 'root') {
      insert = buildRoot(this.problemJS, this.line);
    } else {
      console.error(t + ' not supported');
    }

    if (intoBase) {
      if (textToLeftOfInsert.length !== 0) {
        const grandparentMulti = this.parent!.parent! as Multi;

        grandparentMulti.pushTextOutOfBaseToLeft(
          this.getValueParent()!,
          textToLeftOfInsert
        );
      }

      if (postString.length === 0) {
        this.children = [];
        this.add(insert!);
      } else {
        const pow: MathObject = this.getValueParent()!;
        const powHolder = pow.parent as Multi;

        index = -1;

        for (let i = 0; i < powHolder!.children.length; i++) {
          if (pow === powHolder!.children[i]) {
            index = i;
            break;
          }
        }

        powHolder!.add(insert!, index);
        powHolder!.balanceEmptyTexts?.();

        srcText.setText(postString);
      }

      this.line!.buildSizeRecursive();
    } else {
      this.add(insert!, index + 1);

      if (postString.length > 0) {
        const secondText = buildText(this.problemJS, 0, 0, postString);

        secondText.e = true;
        this.add(secondText, index + 2);
      }
    }

    insert!.buildSizeRecursive();

    if (!intoBase) this.balanceEmptyTexts();

    this.problemJS.maybeUndoSelectedLineEntryButton();

    this.problemJS.storeCurrentLineXML();

    this.line!.notifySizeChanged();

    return insert!;
  }

  buildSizeRecursive(): void {
    for (let i = 0; i < this.getValueChildren().length; i++) {
      const child = this.getValueChildren()[i];

      // texts automatically maintain size
      if (!(child instanceof Text)) {
        child.buildSizeRecursive();
      }
    }

    this.buildSize();
  }

  buildSize() {
    let w = 0;
    let h = 0;
    let upperHeight = 0;
    let lowerHeight = 0;

    for (let i = 0; i < this.getValueChildren().length; i++) {
      w += this.getValueChildren()[i].viewportW;

      h = this.getValueChildren()[i].viewportH;
      let childUpperHeight = h / 2;
      let childLowerHeight = h / 2;

      // Check for non vertically centered
      const vertBaseLine = this.getValueChildren()[i].getVertToBaseline?.();

      if (vertBaseLine) {
        const diff = vertBaseLine - childUpperHeight;

        childUpperHeight += diff;
        childLowerHeight -= diff;
      }

      upperHeight = Math.max(upperHeight, childUpperHeight);
      lowerHeight = Math.max(lowerHeight, childLowerHeight);
    }

    this.setAllDim(w, upperHeight + lowerHeight);
    this.setPositionsOfChildren();
  }

  getFirst(s: string): MathObject | undefined {
    for (let a = 0; a < this.getValueChildren().length; a++) {
      if (this.getValueChildren()[a].t === s) {
        return this.getValueChildren()[a];
      }
    }

    return undefined;
  }

  getVertToBaseline(): number {
    let toB = 0;

    for (let i = 0; i < this.getValueChildren().length; i++) {
      const c = this.getValueChildren()[i];
      const cB = c.getVertToBaseline();

      toB = Math.max(toB, cB);
    }

    return toB;
  }

  setPositionsOfChildren(): void {
    const toB = this.getVertToBaseline();
    let x = 0;

    for (let i = 0; i < this.getValueChildren().length; i++) {
      const c = this.getValueChildren()[i];

      c.viewportY = toB - c.getVertToBaseline();
      c.viewportX = x;
      x += c.viewportW;
    }
  }

  childSizeChanged(): void {
    this.buildSize();
    this.getValueParent()?.childSizeChanged();
  }

  /**
   * Better name would be insertAt
   * @param tile MathObject to insert
   * @param index Location of insert
   */
  add(tile: MathObject, index?: number): void {
    if (index === undefined) index = this.getValueChildren().length;
    this.getValueChildren().splice(index, 0, tile);
    tile.parent = this;
    tile.setLine(this.line!);
  }

  grabFocus(direction?: Direction): void {
    const index =
      direction === 'right' ? 0 : this.getValueChildren().length - 1;

    this.getValueChildren()[index].grabFocus(direction);
  }
}

export function buildMulti(
  problemJS: MultiDependencies,
  line: Line,
  startingText?: string,
  maxW?: number
): Multi {
  const ret = new Multi(problemJS, line);

  const text = buildText(
    problemJS,
    0,
    0,
    startingText || '',
    undefined,
    undefined,
    maxW,
    maxW === undefined
  );

  text.e = true;
  text.parent = ret;
  ret.add(text);

  return ret;
}
